import { useEffect, useRef, useState } from "react";
import { authHeaders } from "./api";
interface RecognitionResultEvent {
  results: {
    [index: number]: {
      isFinal: boolean;
      [index: number]: { transcript: string };
    };
    length: number;
  };
  resultIndex: number;
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
declare global {
  interface Window {
    SpeechRecognition?: new () => Recognition;
    webkitSpeechRecognition?: new () => Recognition;
  }
}
export function useVoice(
  onTranscript: (text: string) => void,
  fallback: boolean,
  onError: (text: string) => void,
) {
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recognition = useRef<Recognition | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const callback = useRef(onTranscript);
  callback.current = onTranscript;
  const release = () => {
    clearTimeout(timer.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    setListening(false);
  };
  const stop = () => {
    recognition.current?.stop();
    if (recorder.current?.state === "recording") recorder.current.stop();
    release();
  };
  const start = async () => {
    if (listening) {
      stop();
      return;
    }
    const RecognitionClass =
      window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (RecognitionClass) {
      const instance = new RecognitionClass();
      recognition.current = instance;
      instance.lang = "en-US";
      instance.continuous = false;
      instance.interimResults = false;
      instance.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++)
          if (e.results[i].isFinal)
            callback.current(e.results[i][0].transcript);
      };
      instance.onerror = (e) => {
        release();
        onError(
          e.error === "not-allowed"
            ? "Microphone permission was denied. You can keep typing."
            : `Dictation stopped (${e.error}). Typed input is available.`,
        );
      };
      instance.onend = release;
      try {
        setListening(true);
        instance.start();
        timer.current = setTimeout(stop, 60000);
      } catch {
        release();
        onError("Dictation could not start. Please keep typing.");
      }
      return;
    }
    if (!fallback || !navigator.mediaDevices?.getUserMedia) {
      onError(
        "Dictation is not supported in this browser. Use a compatible browser or configure server transcription. You can keep typing.",
      );
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const media = new MediaRecorder(stream.current, { mimeType: mime });
      recorder.current = media;
      const chunks: BlobPart[] = [];
      media.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      media.onstop = async () => {
        release();
        setTranscribing(true);
        try {
          const res = await fetch("/api/transcribe", {
            method: "POST",
            headers: { "Content-Type": mime, ...(await authHeaders()) },
            body: new Blob(chunks, { type: mime }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error);
          callback.current(data.text);
        } catch (e) {
          onError(e instanceof Error ? e.message : "Transcription failed.");
        } finally {
          setTranscribing(false);
        }
      };
      media.start();
      setListening(true);
      timer.current = setTimeout(stop, 60000);
    } catch {
      release();
      onError("Microphone access was unavailable. You can keep typing.");
    }
  };
  useEffect(
    () => () => {
      recognition.current?.abort();
      if (recorder.current) {
        recorder.current.onstop = null;
        if (recorder.current.state === "recording") recorder.current.stop();
      }
      clearTimeout(timer.current);
      stream.current?.getTracks().forEach((t) => t.stop());
    },
    [],
  );
  return { listening, transcribing, toggle: start, stop };
}
